
/**
 * Performance-optimized logging utility
 */

const isDev = import.meta.env?.DEV || false;
const isVerbose = import.meta.env?.VITE_VERBOSE_LOGS === 'true' || false;

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  VERBOSE = 4
}

// Only allow verbose logging in development with explicit flag
const currentLogLevel = isDev ? 
  (isVerbose ? LogLevel.VERBOSE : LogLevel.ERROR) : // Reduced default logging in dev
  LogLevel.ERROR; // Production only shows errors

/**
 * Performance-optimized logger that reduces overhead in production
 */
export class PerformanceLogger {
  private context: string;
  
  constructor(context: string) {
    this.context = context;
  }
  
  error(message: string, ...args: any[]): void {
    if (currentLogLevel >= LogLevel.ERROR) {
      console.error(`[${this.context}] ${message}`, ...args);
    }
  }
  
  warn(message: string, ...args: any[]): void {
    if (currentLogLevel >= LogLevel.WARN) {
      console.warn(`[${this.context}] ${message}`, ...args);
    }
  }
  
  info(message: string, ...args: any[]): void {
    if (currentLogLevel >= LogLevel.INFO) {
      console.log(`[${this.context}] ${message}`, ...args);
    }
  }
  
  debug(message: string, ...args: any[]): void {
    if (currentLogLevel >= LogLevel.DEBUG) {
      console.log(`[${this.context}] ${message}`, ...args);
    }
  }
  
  verbose(message: string, ...args: any[]): void {
    if (currentLogLevel >= LogLevel.VERBOSE) {
      console.log(`[${this.context}] ${message}`, ...args);
    }
  }
  
  /**
   * Time a function execution (only in development)
   */
  time<T>(label: string, fn: () => T): T {
    if (currentLogLevel >= LogLevel.DEBUG) {
      const start = performance.now();
      const result = fn();
      const end = performance.now();
      this.debug(`${label}: ${(end - start).toFixed(2)}ms`);
      return result;
    }
    return fn();
  }
  
  /**
   * Time an async function execution (only in development)
   */
  async timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (currentLogLevel >= LogLevel.DEBUG) {
      const start = performance.now();
      const result = await fn();
      const end = performance.now();
      this.debug(`${label}: ${(end - start).toFixed(2)}ms`);
      return result;
    }
    return fn();
  }
}

/**
 * Create a performance logger for a specific context
 */
export function createPerformanceLogger(context: string): PerformanceLogger {
  return new PerformanceLogger(context);
}
